import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { BoardFunnel } from '../../board-v3.types';
import { FUNNEL_STATUS, FUNNEL_TYPE } from '../../../../enum/constants';
import { BoardService } from '../../../../services/board.service';
import { NotificationService } from '../../../../services/notifications.service';

@Component({
  selector: 'app-board-v3-create-funnel',
  templateUrl: './create-funnel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BoardV3CreateFunnelComponent implements OnChanges {
  @Input() funnel: BoardFunnel | null = null;
  @Input() funnelType: FUNNEL_TYPE = FUNNEL_TYPE.DEAL;

  @Output() close = new EventEmitter<{ funnel: BoardFunnel } | void>();

  private readonly boardService = inject(BoardService);
  private readonly notificationService = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly nameControl = new FormControl('', { nonNullable: true, validators: [Validators.required] });

  isSaving = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['funnel']) {
      this.nameControl.setValue(this.funnel?.name ?? '');
      this.nameControl.markAsUntouched();
    }
  }

  get isEditMode(): boolean {
    return !!this.funnel;
  }

  onClose(): void {
    if (!this.isSaving) this.close.emit();
  }

  onSave(): void {
    if (this.isSaving) return;

    const name = this.nameControl.value.trim();
    if (!name) {
      this.nameControl.markAsTouched();
      return;
    }

    const payload = { name };

    const funnel = this.funnel;
    this.isSaving = true;
    const request = funnel
      ? this.boardService.updateFunnel(funnel.id, payload)
      : this.boardService.createFunnel({ ...payload, type: this.funnelType, status: FUNNEL_STATUS.DEFAULT });

    request
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.isSaving = false)),
      )
      .subscribe({
        next: (savedFunnel) => this.close.emit({ funnel: savedFunnel }),
        error: () => this.notificationService.showError('', 'Failed to save funnel'),
      });
  }
}
